import React from 'react'
import { Pagination } from '../../interfaces/pagination'

export interface IProps extends Pagination {
  size?: 'sm' | 'lg'
  disabled?: boolean
  onChange: (pagination: Pagination) => void
}

export const PageSelector = (props: IProps): React.ReactElement | null => {
  const {
    size,
    count,
    total,
    limit,
    page,
    page_count: queryPageCount = 0,
    disabled,
    onChange,
  } = props
  const page_count = queryPageCount - 1
  const items: number[] = []
  const isTruncated: boolean = page_count > 6
  const truncateLeft: number = page_count - page < 4 ? page_count - 5 : page >= 5 ? page - 2 : 0
  const truncateRight = page < 4 ? 6 : page_count - page >= 4 ? page + 2 : 0
  let className = `pagination my-2 mx-auto`
  if (size === 'sm') {
    className += ' pagination-sm'
  } else if (size === 'lg') {
    className += ' pagination-lg'
  }
  for (let i = 0; i <= (page_count || 0); i++) {
    items.push(i)
  }
  const handleClick = (value: number) => {
    if (page !== value) {
      onChange({
        limit,
        count,
        total,
        page: value,
        page_count,
      })
    }
    if (window) {
      window?.scrollTo(0, 0)
    }
  }

  if (count >= total) {
    return null
  }

  return (
    <div className="d-flex mt-3" style={{ width: '100%', overflow: 'auto' }}>
      <ul className={className}>
        {items.map((item) => (
          <li
            key={`page-item-${item}`}
            className={['page-item', page === item ? 'active' : ''].join(' ').trim()}
          >
            {isTruncated ? (
              item === 0 || item === page_count || (item > truncateLeft && item < truncateRight) ? (
                <span
                  className={['btn btn-link page-link mx-1', disabled ? 'disabled' : '']
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => handleClick(item)}
                >
                  {item + 1}
                </span>
              ) : !!truncateLeft && !truncateRight && item > truncateLeft ? (
                <span
                  className={['btn btn-link page-link mx-1', disabled ? 'disabled' : '']
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => handleClick(item)}
                >
                  {item + 1}
                </span>
              ) : !!truncateRight && !truncateLeft && item < truncateRight ? (
                <span
                  className={['btn btn-link page-link mx-1', disabled ? 'disabled' : '']
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => handleClick(item)}
                >
                  {item + 1}
                </span>
              ) : item === truncateRight || item === truncateLeft ? (
                <span className="page-link page-separator mx-1">···</span>
              ) : null
            ) : (
              <span
                className={['btn btn-link page-link mx-1', disabled ? 'disabled' : '']
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => handleClick(item)}
              >
                {item + 1}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

interface IPageNavSelectorProps extends Omit<IProps, 'total' | 'page_count'> {
  hasMore: boolean
}

export const PageNavSelector = (props: IPageNavSelectorProps): React.ReactElement | null => {
  const { size, count, limit, page, disabled, hasMore, onChange } = props
  let className = `pagination my-2`
  if (size === 'sm') {
    className += ' pagination-sm'
  } else if (size === 'lg') {
    className += ' pagination-lg'
  }

  if (page === 0 && !hasMore) {
    return null
  }

  const handleClick = (value: number) => {
    if (page !== value) {
      onChange({
        limit,
        count,
        total: 0,
        page: value,
        page_count: 0,
      })
    }
    if (window) {
      window?.scrollTo(0, 0)
    }
  }

  const isPreviousDisabled = page === 0 || disabled
  const isNextDisabled = !hasMore || disabled

  return (
    <div className="d-flex mt-3 py-3 justify-content-center">
      <button
        type="button"
        className={['btn btn-link mx-1', isPreviousDisabled ? 'disabled' : '']
          .filter(Boolean)
          .join(' ')}
        onClick={() => {
          if (isPreviousDisabled) return
          handleClick(page - 1)
        }}
      >
        Previous
      </button>
      <ul className={className}>
        <li className="page-item active">
          <span className="page-link">{page + 1}</span>
        </li>
      </ul>
      <button
        type="button"
        className={['btn btn-link mx-1', isNextDisabled ? 'disabled' : '']
          .filter(Boolean)
          .join(' ')}
        onClick={() => {
          if (isNextDisabled) return
          handleClick(page + 1)
        }}
      >
        Next
      </button>
    </div>
  )
}
